import React, { useState, useEffect } from "react";
import { documentAPI } from "../services/api";
import { toast } from "react-toastify";
import {
  FileText,
  Calendar,
  Shield,
  TrendingUp,
  Plus,
  Filter,
} from "lucide-react";
import { safeFormatDateOnly } from "../utils/dateUtils";

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      console.log("Fetching dashboard data...");
      const [statsResponse, documentsResponse] = await Promise.all([
        documentAPI.getStats(),
        documentAPI.getMyDocuments(),
      ]);

      console.log("Stats response:", statsResponse);
      console.log("Documents response:", documentsResponse);

      setStats(statsResponse.data);

      // Handle both array and object response formats
      const documentsData = documentsResponse.data;
      let documents = [];
      if (Array.isArray(documentsData)) {
        documents = documentsData;
      } else if (documentsData && Array.isArray(documentsData.documents)) {
        documents = documentsData.documents;
      }

      console.log("Setting recent documents:", documents);
      setRecentDocuments(documents.slice(0, 5));
    } catch (error) {
      console.error("Dashboard fetch error details:", error);
      console.error("Error response:", error.response);
      console.error("Error status:", error.response?.status);
      console.error("Error data:", error.response?.data);
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-gray-600">
            Welcome to your digital signature platform
          </p>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="card p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-8 w-8 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Total Documents
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalDocuments || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Shield className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Verified Documents
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.verifiedDocuments || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Calendar className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    This Month
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.thisMonthCount || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Success Rate
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalDocuments > 0
                      ? Math.round(
                          ((stats.verifiedDocuments || 0) /
                            stats.totalDocuments) *
                            100
                        )
                      : 0}
                    %
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="card p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Actions
          </h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => (window.location.href = "/documents/sign")}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Sign New Document</span>
            </button>
            <button
              onClick={() => (window.location.href = "/documents")}
              className="btn-secondary flex items-center space-x-2"
            >
              <Filter className="h-4 w-4" />
              <span>View All Documents</span>
            </button>
          </div>
        </div>

        {/* Recent Documents */}
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Documents
            </h2>
          </div>
          {recentDocuments.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No documents signed yet</p>
              <button
                onClick={() => (window.location.href = "/documents/sign")}
                className="mt-4 btn-primary"
              >
                Sign Your First Document
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {recentDocuments.map((doc) => (
                <div key={doc._id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">
                        {doc.documentName}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {doc.description || "No description"}
                      </p>
                      <div className="flex items-center mt-2 space-x-4 text-xs text-gray-500">
                        <span>Type: {doc.documentType || "PDF"}</span>
                        <span>
                          Size:{" "}
                          {doc.fileSize
                            ? (doc.fileSize / 1024).toFixed(1) + " KB"
                            : "Unknown"}
                        </span>
                        <span>Signed: {safeFormatDateOnly(doc.signedAt)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Signed
                      </span>
                      <button
                        onClick={() =>
                          (window.location.href = `/documents/${doc._id}`)
                        }
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
